import React, { useContext, useEffect } from 'react';
import GetState from '../../GetState';
import { QuestRoute } from '../../api/QuestRoutes';
import Form from '../../forms/Form';
import Checkbox from '../../forms/Checkbox';
import Radio from '../../forms/Radio';
import Input from '../../forms/Input';
import Preput from '../../forms/Preput';
import { formatCurrency, generateId } from '../../utility/functions';
import Button from '../../forms/Button';
import Table, { Ctd } from '../../table/Table';
import HamQuest from '../../api/HamQuestGiver';
import LocationSelectors from '../../forms/formComponents/LocationSelectors';
import ImageIndex from '../../forms/formComponents/ImageIndex';
import ColorInput from '../../forms/formComponents/ColorInput';
import TerminalIDsSelector from '../../forms/formComponents/TerminalIDsSelector';
import { userRoles } from '../../RoleProtected';
import ManagementRow from '../../table/ManagementRow';
import Select from '../../forms/Select';
import Row from '../../layout/Row';
import { HSOptions } from '../../../constants/HSOptions';
import ModalFactory from '../../Modal';
import AllowedFeatures, { featureNames } from '../../navigation/AllowedFeatures';
import CheckMark from '../../CheckMark';
import { Features, HasFeature } from '../../RequireFeature';
import { FormContext } from '../../forms/Form';
import UpgradeAndAutoSelect from '../../forms/formComponents/UpgradeAndAutoSelect';

const deriveWashClubMaxUseMode = value =>
    (value === null || value === undefined || value === '') ? 'Unlimited' : (Number(value) === 0 ? 'None' : 'Custom');

const deriveWashClubMaxUseModes = club => ({
    ...club,
    appMaxUsesMode: deriveWashClubMaxUseMode(club.appMaxUses),
    passMaxUsesMode: deriveWashClubMaxUseMode(club.passMaxUses),
    maxRedemptionsMode: deriveWashClubMaxUseMode(club.maxRedemptions),
    tellerReuseLimitMode: deriveWashClubMaxUseMode(club.tellerReuseLimit),
    timerMaxUsesMode: deriveWashClubMaxUseMode(club.timerMaxUses),
    timerReuseLimitMode: deriveWashClubMaxUseMode(club.timerReuseLimit),
});

const Management = ({ userData, ...props }) => {

    const [details] = GetState(QuestRoute.washClubs.detail)
    const [washClubs, refreshWC] = GetState(QuestRoute.washClubs.clubs)
    const hasNewMaxUses = HasFeature(Features.NewMaxUses);

    const deleteWashClub = (club) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${club.productName}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Wash Club',
                body: `Deleting ${club.productName}`,
                callback: () => HamQuest.delete(QuestRoute.washClubs.edit(club.productId))
            },
            confirmed: {
                title: 'Success',
                body: `${club.productName} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshWC()
        })
    }

    const WCForm = ({ values, ...props }) => {
        if (values.setMaxUses === undefined) {
            values.setMaxUses = values.maxUses ? true : false;
        }
        const hasPAYG = HasFeature(Features.PAYG);
        const hasWCTime = HasFeature(Features.WashClubTime);
        const hasNewMaxUses = HasFeature(Features.NewMaxUses);

        const { setValues } = useContext(FormContext);
        useEffect(() => {
            if (values.maxRedemptionsMode !== 'Custom') return;
            const app = values.appMaxUsesMode === 'Custom' && values.appMaxUses !== '' && values.appMaxUses != null ? Number(values.appMaxUses) : null;
            const pass = values.passMaxUsesMode === 'Custom' && values.passMaxUses !== '' && values.passMaxUses != null ? Number(values.passMaxUses) : null;
            if (app !== null && pass !== null && !isNaN(app) && !isNaN(pass)) {
                setValues(v => ({ ...v, maxRedemptions: String(app + pass) }));
            }
        }, [values.appMaxUses, values.passMaxUses, values.appMaxUsesMode, values.passMaxUsesMode, values.maxRedemptionsMode]);
        const washClubTypeOptions = HSOptions.washClub.type.filter(
            o => o.value !== 'DailyPass' || hasPAYG
        );
        const washClubAwardTypeOptions = HSOptions.washClub.awardType.filter(
            o => o.value !== 'ItemRate' || hasPAYG
        );
        const maxUseModeOptions = HSOptions.washClub.maxUseMode.filter(o => o.value !== 'UseDefault');
        const showMaxRedemptions = values.appMaxUsesMode === 'Unlimited' || values.passMaxUsesMode === 'Unlimited';

        return (
            <>
                <Checkbox
                    label='Active'
                    name='active'
                />

                {(hasWCTime || hasPAYG) && (
                    <Select
                        label='Type'
                        name='type'
                        options={washClubTypeOptions}
                        required
                        disable={props.edit}
                    />
                )}

                <Select
                    label='Award Type'
                    name='awardType'
                    options={washClubAwardTypeOptions}
                />
                { values.awardType === 'ItemRate' && (
                    <Input
                        label='Award Value'
                        name='awardValue'
                        type='price'
                        min='0'
                    />
                )}


                <Radio
                    label='Item'
                    name='itemId'
                    options={details?.washes}
                    required
                />
                <Checkbox
                    label='Use Item Name'
                    name='productUseItemName'
                />
                <Input
                    label='Name'
                    name='productName'
                    disable={values.productUseItemName}
                    required={!values.productUseItemName}
                />
                <Checkbox
                    label='Use Product Details'
                    name='productUseItemDetail'
                />
                <Preput
                    label='Details'
                    name='details'
                    disabled={values.productUseItemDetail}
                />
                {values.type === 'DailyPass' ?
                <div>
                <Input
                    label='Sign-up Price'
                    name='signupPrice'
                    type='price'
                    min='0'
                />
                <Input
                    label={'Unit Price'}
                    name='monthlyPrice'
                    type='price'
                    tooltipText='What the customer will be charged for the wash.'
                    required={values.type === 'DailyPass'}
                />
                </div>
                :
                <div>
                <Input
                    label='Sign-up Price'
                    name='signupPrice'
                    type='price'
                    min='0'
                    required
                />
                <Input
                    label={'Monthly Price'}
                    name='monthlyPrice'
                    type='price'
                    required
                />

                {values.type !== '5' && (
                    <Input
                        label='Additional Car Price'
                        name='additionalCarPrice'
                        type='price'
                        placeholder={values.monthlyPrice}
                    />
                )}
                </div>
            }

                <AllowedFeatures feature={featureNames.taxableWashClub}>
                    <Checkbox
                        label='Is taxed'
                        name='taxable'
                    />
                </AllowedFeatures>

                {values.type !== 'DailyPass' && (
                    hasNewMaxUses ? (
                        <div>
                            <Row wrap>
                                <Select
                                    label='App Monthly Uses Mode'
                                    name='appMaxUsesMode'
                                    options={maxUseModeOptions}
                                    required
                                />
                                {values.appMaxUsesMode === 'Custom' ? (
                                    <Input
                                        label='App Monthly Uses'
                                        name='appMaxUses'
                                        type='number'
                                        min={1}
                                        required
                                        tooltipText='Maximum App redemptions that can be vended in a given billing period.'
                                    />
                                ) : (
                                    <Input
                                        label='App Monthly Uses'
                                        value={values.appMaxUsesMode === 'None' ? '0' : ''}
                                        disable
                                    />
                                )}
                            </Row>
                            <Row wrap>
                                <Select
                                    label='Pass Monthly Uses Mode'
                                    name='passMaxUsesMode'
                                    options={maxUseModeOptions}
                                    required
                                />
                                {values.passMaxUsesMode === 'Custom' ? (
                                    <Input
                                        label='Pass Monthly Uses'
                                        name='passMaxUses'
                                        type='number'
                                        min={1}
                                        required
                                        tooltipText='Maximum Pass redemptions that can be vended in a given billing period.'
                                    />
                                ) : (
                                    <Input
                                        label='Pass Monthly Uses'
                                        value={values.passMaxUsesMode === 'None' ? '0' : ''}
                                        disable
                                    />
                                )}
                            </Row>
                            {showMaxRedemptions && (
                                <Row wrap>
                                    <Select
                                        label='Total Monthly Redemptions Mode'
                                        name='maxRedemptionsMode'
                                        options={maxUseModeOptions}
                                        required
                                    />
                                    {values.maxRedemptionsMode === 'Custom' ? (
                                        <Input
                                            label='Total Monthly Redemptions'
                                            name='maxRedemptions'
                                            type='number'
                                            min={1}
                                            required
                                            tooltipText='Maximum total redemptions (Pass + App) per billing period. Only used when App Monthly Uses or Pass Monthly Uses is Unlimited.'
                                        />
                                    ) : (
                                        <Input
                                            label='Total Monthly Redemptions'
                                            value={values.maxRedemptionsMode === 'None' ? '0' : ''}
                                            disable
                                        />
                                    )}
                                </Row>
                            )}
                            <Row wrap>
                                <Select
                                    label='Kiosk Daily Use Limit Mode'
                                    name='tellerReuseLimitMode'
                                    options={maxUseModeOptions}
                                    required
                                />
                                {values.tellerReuseLimitMode === 'Custom' ? (
                                    <Input
                                        label='Kiosk Daily Use Limit'
                                        name='tellerReuseLimit'
                                        type='number'
                                        min={1}
                                        required
                                        tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the teller.'
                                    />
                                ) : (
                                    <Input
                                        label='Kiosk Daily Use Limit'
                                        value={values.tellerReuseLimitMode === 'None' ? '0' : ''}
                                        disable
                                    />
                                )}
                            </Row>
                            <Row wrap>
                                <Select
                                    label='Timer Monthly Uses Mode'
                                    name='timerMaxUsesMode'
                                    options={maxUseModeOptions}
                                    required
                                />
                                {values.timerMaxUsesMode === 'Custom' ? (
                                    <Input
                                        label='Timer Monthly Uses'
                                        name='timerMaxUses'
                                        type='number'
                                        min={1}
                                        required
                                    />
                                ) : (
                                    <Input
                                        label='Timer Monthly Uses'
                                        value={values.timerMaxUsesMode === 'None' ? '0' : ''}
                                        disable
                                    />
                                )}
                            </Row>
                            <Row wrap>
                                <Select
                                    label='Timer Daily Use Limit Mode'
                                    name='timerReuseLimitMode'
                                    options={maxUseModeOptions}
                                    required
                                />
                                {values.timerReuseLimitMode === 'Custom' ? (
                                    <Input
                                        label='Timer Daily Use Limit'
                                        name='timerReuseLimit'
                                        type='number'
                                        min={1}
                                        required
                                        tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the timers.'
                                    />
                                ) : (
                                    <Input
                                        label='Timer Daily Use Limit'
                                        value={values.timerReuseLimitMode === 'None' ? '0' : ''}
                                        disable
                                    />
                                )}
                            </Row>
                        </div>
                    ) : (
                        <div>
                            <Checkbox
                                label='Set Max Uses'
                                name='setMaxUses'
                            />
                            <Input
                                label='Max Uses'
                                name='maxUses'
                                type='number'
                                disabled={!values.setMaxUses}
                                required={values.setMaxUses}
                            />
                            <Input
                                label='Timer Max Uses'
                                name='timerMaxUses'
                                type='number'
                                disable={!values.setMaxUses}
                            />
                        </div>
                    )
                )}

                {hasWCTime && (
                    <Input
                        label='Wash Club Time (m)'
                        name='washClubTime'
                        type='number'
                    />
                )}

                <b>Allowed Models</b>
                {details?.kioskSubtypes?.map((e, i) => (
                    <Checkbox
                        key={'kioskSubtypes' + i}
                        name='kioskSubTypeIds'

                        label={e.label}
                        value={e.value}
                    />
                ))}

                <ImageIndex />

                <ColorInput />


                <UpgradeAndAutoSelect 
                    isAward={true} 
                    autoSelectFieldName='autoSelect'
                />

                <Input
                    label='SKU'
                    name='sku'
                />

                {/* <Checkbox
                    label='Using RFID Tags?'
                    name='isFulfillable'
                /> */}

                <LocationSelectors />

                <TerminalIDsSelector />
            </>
        )
    }

    const editWashClub = (club) => {
        club.washClubTime = Math.floor(club.washClubTime / 60);
        const initialInputs = deriveWashClubMaxUseModes(club);
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${club.productName}`,
            body: (
                <Form
                    name='washClubSettings'
                    inputs={initialInputs}
                    onSubmit={e => {
                        e.washClubTime = e.washClubTime * 60;

                        const maxUseFields = [
                            'appMaxUsesMode', 'appMaxUses',
                            'passMaxUsesMode', 'passMaxUses',
                            'maxRedemptionsMode', 'maxRedemptions',
                            'tellerReuseLimitMode', 'tellerReuseLimit',
                            'timerMaxUsesMode', 'timerMaxUses',
                            'timerReuseLimitMode', 'timerReuseLimit'
                        ];
                        const hasMaxUseChanges = hasNewMaxUses && maxUseFields.some(
                            field => String(e[field] ?? '') !== String(initialInputs[field] ?? '')
                        );

                        const submit = (applyToEveryone) => {
                            if (applyToEveryone) e.ApplyMaxUseSettingsToEveryone = true;
                            return HamQuest.put(QuestRoute.washClubs.edit(club.productId), { ...e })
                                .then(() => {
                                    refreshWC();
                                    ModalFactory.close(id);
                                });
                        };

                        if (!hasMaxUseChanges) return submit(false);

                        return new Promise((resolve) => {
                            const promptId = generateId();
                            ModalFactory.show({
                                id: promptId,
                                title: 'Apply to Modified Users?',
                                body: (
                                    <div>
                                        <p>Do you want to apply these max use changes to users who have had their max uses modified?</p>
                                        <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                                            <Button onClick={() => { ModalFactory.close(promptId); resolve(submit(true)); }}>Yes</Button>
                                            <Button onClick={() => { ModalFactory.close(promptId); resolve(submit(false)); }}>No</Button>
                                        </div>
                                    </div>
                                )
                            });
                        });
                    }}
                    text='Update'
                >
                    {({ values }) => <WCForm values={values} edit={true}/>}
                </Form>
            )
        })
    }

    const addWashClub = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add Wash Club`,
            body: (
                <Form
                    name='washClubSettings'
                    inputs={{
                        active: true,
                        type: 'AppWashClub',
                        awardType: 'Item',
                        awardValue: '',
                        itemId: '',
                        productUseItemName: false,
                        productName: '',
                        productUseItemDetail: false,
                        details: '',
                        signupPrice: '',
                        monthlyPrice: '',
                        additionalCarPrice: '',
                        taxable: false,
                        setMaxUses: false,
                        maxUses: '',
                        timerMaxUses: '',
                        timerMaxUsesMode: 'Unlimited',
                        appMaxUses: '',
                        appMaxUsesMode: 'Unlimited',
                        passMaxUses: '',
                        passMaxUsesMode: 'Unlimited',
                        maxRedemptions: '',
                        maxRedemptionsMode: 'Unlimited',
                        tellerReuseLimit: '',
                        tellerReuseLimitMode: 'Unlimited',
                        timerReuseLimit: '',
                        timerReuseLimitMode: 'Unlimited',
                        washClubTime: '',
                        logoImageIndex: '',
                        logoColor: '',
                        allowUpgrades: '',
                        autoSelect: '',
                        kioskSubTypeIds: [],
                        locations: [],
                        terminalIds: [],
                    }}
                    onSubmit={e => {
                        e.washClubTime = e.washClubTime * 60;
                        return HamQuest.post(QuestRoute.washClubs.clubs, { ...e })
                            .then(() => {
                                refreshWC();
                                ModalFactory.close(id);
                            })
                    }}
                    text='Add'
                >
                    {({ values }) => <WCForm values={values} edit={false} />}
                </Form>
            )
        })
    }

    const tableConfig = {
        data: washClubs,
        controls: [<Button roles={userRoles.editProducts} onClick={addWashClub}>Add Wash Club</Button>],
        // disableHeaderSearch: true,
        colSpacing: [20, 15],
        headers: [
            {
                label: 'Name',
                propKey: 'productName',
            }, {
                label: 'Kiosk Item',
                propKey: 'kioskItemName',
                options: true
            }, {
                label: 'Signup Price',
                propKey: 'signupPrice',
                type: 'number'
            }, {
                label: 'Monthly Price',
                propKey: 'monthlyPrice',
                type: 'number'
            }, {
                label: 'Add. Car Price',
                propKey: 'additionalCarPrice',
                type: 'number'
            }, {
                label: 'Active',
                propKey: 'active',
                options: true,
            }, {
                label: 'Max Uses',
                propKey: 'maxUses',
                type: 'number'
            }, {
                label: 'Timer Max Uses',
                propKey: 'timerMaxUses',
                type: 'number'
            },
        ],
        quickSort: [
            {
                label: 'Active Clubs',
                propKey: 'active',
                query: 'true',
                color: '#21D59B',
                defaultSearch: true
            },
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={[
                    <Button roles={userRoles.editProducts} key={generateId()} onClick={() => editWashClub(e)}>Edit</Button>,
                    <Button roles={userRoles.editProducts} key={generateId()} onClick={() => deleteWashClub(e)}>Delete</Button>
                ]}
            >
                <td>{e.productName}</td>
                <td>{e.kioskItemName}</td>
                <td>{formatCurrency(e.signupPrice)}</td>
                <td>{formatCurrency(e.monthlyPrice)}</td>
                <td>{e.additionalCarPrice ? formatCurrency(e.additionalCarPrice) : '-'}</td>
                <Ctd><CheckMark checked={e.active} /></Ctd>
                <td>{(hasNewMaxUses ? e.maxRedemptions : e.maxUses) ?? '-'}</td>
                <td>{e.timerMaxUses ?? '-'}</td>
            </ManagementRow>
        )
    }

    return (
        <Table config={tableConfig} />
    );
}

export default Management;
